<template>
  <div
    v-if="!fullyBooked"
    class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
  >
    <div
      class="flex justify-center text-13 leading-120 font-bold whitespace-nowrap"
    >
      <span class="pr-4">Available for:</span>
      <span v-if="!soldOutCalls" class="text-accent-green">1-1 Video Call</span>
      <span
        v-if="!soldOutCalls && !soldOutFeedbacks"
        class="text-accent-green px-2"
        >&middot;</span
      >
      <span v-if="!soldOutFeedbacks" class="text-accent-green">Feedback</span>
    </div>
    <div
      class="bg-accent-orange hover:bg-accent-orange-hover rounded-[63px] flex justify-between text-grayscale-7 px-5 py-13px lg:min-w-255px cursor-pointer"
      @click="scrollToProducts"
    >
      <p class="font-semibold text-14 leading-160">
        Book
        <span class="font-bold">{{
          pro.firstName.length > 10 ? "now" : pro.firstName
        }}</span>
      </p>
      <p class="font-normal text-14 leading-160">
        From
        <span v-if="cheapestProducts" class="font-bold"
          >${{ cheapestProducts[0].price }}</span
        >
      </p>
    </div>
  </div>
  <div
    v-else
    class="px-4 border border-accent-red/50 bg-accent-red/[0.15] h-20 lg:h-auto rounded-8 text-grayscale-96 flex flex-col lg:flex-row items-center lg:justify-start justify-center gap-0.5 lg:gap-2"
  >
    <p class="font-semibold text-sm text-center">
      {{ pro.firstName }} is fully booked this week
    </p>
    <p class="text-13 text-center">More availability will be added soon</p>
  </div>
</template>
<script setup lang="ts">
import type { User } from "../../../types/user";
import { ProductType } from "@/types/product";
import { useProAvailabilityStore } from "@/store/pro-availability";
import { useUserStore } from "@/store/user";
import { useProStore } from "@/store/pros";

const props = defineProps({
  pro: {
    type: Object as PropType<User>,
    required: true,
  },
});

const emit = defineEmits(["scrollToProducts"]);

const proStore = useProStore();
const proAvailabilityStore = useProAvailabilityStore();
const userStore = useUserStore();

const pro = computed(() => proStore.pro);

const soldOutFeedbacks = ref(false);
const soldOutCalls = ref(false);
const fullyBooked = computed(
  () => soldOutFeedbacks.value && soldOutCalls.value,
);

const cheapestProducts = ref();
const scrollToProducts = () => {
  emit("scrollToProducts");
};

const searchMinPrice = (types: ProductType[]) => {
  const prices = pro.value?.products
    ?.filter((product) => types.includes(product.type) && product.isAvailable)
    .sort((a, b) => a.price - b.price);
  return prices;
};

onMounted(async () => {
  await proAvailabilityStore.getProCalendarAvailability(
    pro.value,
    userStore.userData.timezone,
  );

  cheapestProducts.value = searchMinPrice([
    ProductType.shortCall,
    ProductType.longCall,
    ProductType.video,
    ProductType.audio,
    ProductType.written,
  ]);

  soldOutFeedbacks.value = Boolean(
    !pro.value.products?.find(
      (product) =>
        product.isAvailable &&
        [ProductType.written, ProductType.audio, ProductType.video].includes(
          product.type,
        ),
    ) ||
      (pro.value?.feedBackCount !== undefined &&
        pro.value?.feedBacksPerWeek !== undefined &&
        pro.value?.feedBackCount >= pro.value?.feedBacksPerWeek),
  );
  soldOutCalls.value = Boolean(
    !pro.value.products?.find(
      (product) =>
        product.isAvailable &&
        [ProductType.shortCall, ProductType.longCall].includes(product.type),
    ) ||
      (pro.value?.consultationCallCount !== undefined &&
        pro.value?.consultationCallsPerWeek !== undefined &&
        pro.value?.consultationCallCount >=
          pro.value?.consultationCallsPerWeek),
  );
});
</script>
